import { useEffect, useMemo } from 'react'
import { AnimatePresence } from 'framer-motion'
import { faTrophy } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import CompareVerdictPanel from '../compare-verdict-panel/CompareVerdictPanel'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { buildCompareRows } from './helpers'
import {
  Backdrop,
  Card,
  Header,
  Title,
  CloseButton,
  Body,
  TitleStrip,
  SideTitle,
  RowGrid,
  RowLabel,
  Cell,
  WinnerGlyph,
} from './styled'
import { CompareModalProps } from './types'

/**
 * Side-by-side compare modal for two imported listings. Builds rows
 * that highlight which side wins each axis (lower price, higher match,
 * being available). Closes via the × button, the backdrop, or Esc.
 *
 * @param {CompareModalProps} props
 * @returns {JSX.Element | null}
 */
const CompareModal = ({
  alpha,
  beta,
  onClose,
}: CompareModalProps): JSX.Element | null => {
  const { profile } = useQuickPassProfile()

  useEffect(() => {
    if (alpha === null || beta === null) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [alpha, beta, onClose])

  const rows = useMemo(() => {
    if (alpha === null || beta === null) {
      return []
    }
    return buildCompareRows(alpha, beta, profile?.unitPreferences ?? null)
  }, [alpha, beta, profile])

  const stopPropagation = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation()
  }

  if (alpha === null || beta === null) {
    return null
  }

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Card
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.28 },
          }}
          exit={{ opacity: 0, y: 20, scale: 0.97 }}
          onClick={stopPropagation}
        >
          <Header>
            <Title>Compare two places</Title>
            <CloseButton
              type="button"
              onClick={onClose}
              aria-label="Close compare"
            >
              ×
            </CloseButton>
          </Header>

          <Body>
            <CompareVerdictPanel
              alpha={alpha}
              beta={beta}
              preferences={profile?.unitPreferences ?? null}
            />

            <TitleStrip>
              <RowLabel>Listing</RowLabel>
              <SideTitle>{alpha.title}</SideTitle>
              <SideTitle>{beta.title}</SideTitle>
            </TitleStrip>

            {rows.map(row => (
              <RowGrid key={row.label}>
                <RowLabel>{row.label}</RowLabel>
                <Cell isWinner={row.alphaWins}>
                  {row.alphaValue}
                  {row.alphaWins === true ? (
                    <WinnerGlyph aria-hidden="true">
                      <Icon icon={faTrophy} />
                    </WinnerGlyph>
                  ) : null}
                </Cell>
                <Cell isWinner={row.betaWins}>
                  {row.betaValue}
                  {row.betaWins === true ? (
                    <WinnerGlyph aria-hidden="true">
                      <Icon icon={faTrophy} />
                    </WinnerGlyph>
                  ) : null}
                </Cell>
              </RowGrid>
            ))}
          </Body>
        </Card>
      </Backdrop>
    </AnimatePresence>
  )
}

export default CompareModal
